import React, { useRef, useLayoutEffect, useState } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    Box,
    Button,
    Typography,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    Tooltip,
    IconButton,
    Paper,
    Dialog,
    DialogContent,
    DialogActions,
    Checkbox,
    Divider,
     Autocomplete,
     TextField,
     Popper
} from '@mui/material';
import { axios_tenant } from "../../../axios";
import { toast } from 'react-toastify';
import ExcelJS from "exceljs";

// Sub Org options: Only US and UK, allow multi-select
const subOrgOptions = [
    { value: 'US', label: 'US' },
    { value: 'UK', label: 'UK' }
];

// Matter types for single and both sub orgs
const allMatterTypes = [
    { value: 'PATENT', label: 'Patent' },
    { value: 'TRADEMARK', label: 'Trademark' },
    { value: 'DESIGN', label: 'Design' },
    { value: 'GENERAL', label: 'General' },
    { value: 'COPYRIGHT', label: 'Copyright' },
    { value: 'Matter with Opposing Parties', label: 'Matter with Opposing Parties' }
];
const generalMatterTypes = [
    { value: 'GENERAL', label: 'General' },
    { value: 'COPYRIGHT', label: 'Copyright' },
    { value: 'Matter with Opposing Parties', label: 'Matter with Opposing Parties' }
];

export default function Generate() {
    const [mode, setMode] = useState('single'); // 'single' or 'bulk'
    const [reason, setReason] = useState('');
    const [subOrg, setSubOrg] = useState([]);
    const [ipType, setIpType] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [fileNumber, setFileNumber] = useState('');
    const [fileNumbers, setFileNumbers] = useState([]); // For bulk
    const [displayBulkNumbers, setDisplayBulkNumbers] = useState(''); // For bulk
    const [loading, setLoading] = useState(false);
    const [bulkCount, setBulkCount] = useState(1);

    // Determine allowed matter types based on subOrg selection
    const filteredIpTypeOptions =
        subOrg.length === 2
            ? generalMatterTypes
            : allMatterTypes;

    // Reset ipType if not allowed for selected subOrg(s)
    React.useEffect(() => {
        if (
            ipType &&
            !filteredIpTypeOptions.some(opt => opt.value === ipType)
        ) {
            setIpType('');
        }
        // eslint-disable-next-line
    }, [subOrg]);

    const isGenerateDisabled =
        subOrg.length === 0 ||
        !ipType ||
        loading ||
        (mode === 'single' ? !reason : !bulkCount || bulkCount < 1 || bulkCount > 50);

    const handleGenerate = async () => {
        setLoading(true);
        try {
            const subOrgString = subOrg.join(',');
            const payload = {
                matterType: ipType,
                subOrg: subOrgString,
                bulk: mode === 'bulk',
                count: mode === 'bulk' ? bulkCount : 1,
                ...(mode === 'single' && { reason }),
            };
            const res = await axios_tenant.post('/file-number-generator/generate', payload);
            const data = await res.data;
            if (mode === 'single') {
                setFileNumber(data.sequence);
            } else {
                setFileNumbers(data.numbers || []);
                setDisplayBulkNumbers(data.display || '');
            }
            setModalOpen(true);
        } catch (e) {
            toast.error('Failed to generate family number(s)');
        } finally {
            setLoading(false);
        }
    };

     const handleSaveFileNumber = async () => {
        try {
            const payload = mode === 'single'
                ? { fileNumber }
                : { fileNumber: fileNumbers, bulk: true };
            await axios_tenant.post('/file-number-generator/save', payload);
            if(mode === 'single') {
                toast.success('Family Number saved!');
            } else {
                toast.success(`Family Numbers ${displayBulkNumbers} saved.`, { autoClose: 4000 });
            }
            setReason('');
            setBulkCount(1);
            setSubOrg([]);
            setIpType('');
            setModalOpen(false);
            setFileNumber('');
            setFileNumbers([]);
            setDisplayBulkNumbers('');
        } catch (e) {
            toast.error('Failed to save family number(s)');
        }
    };

    const handleDeleteFileNumber = async () => {
        try {
            const payload = mode === 'single'
                ? { fileNumber }
                : { fileNumber: fileNumbers, bulk: true };
            await axios_tenant.post('/file-number-generator/delete', payload);
            if (mode === 'single') {
                toast.info('Family Number deleted.', { autoClose: 5000 });
            } else {
                toast.info(`Family Numbers ${displayBulkNumbers} deleted.`, { autoClose: 5000 });
            }
            setReason('');
            setBulkCount(1);
            setSubOrg([]);
            setIpType('');
            setModalOpen(false);
            setFileNumber('');
            setFileNumbers([]);
            setDisplayBulkNumbers('');
        } catch (e) {
            toast.error('Failed to delete family number(s)');
        }
    };

    // Custom Popper that sets maxHeight based on available space below input
    function AdaptivePopper(props) {
        const popperRef = useRef(null);
        const [maxHeight, setMaxHeight] = useState();

        useLayoutEffect(() => {
            if (props.anchorEl) {
                const anchorRect = props.anchorEl.getBoundingClientRect();
                const spaceBelow = window.innerHeight - anchorRect.bottom - 8; // 8px margin
                setMaxHeight(spaceBelow > 100 ? spaceBelow : 100); // fallback min height
            }
        }, [props.anchorEl, props.open]);

        return (
            <Popper
                {...props}
                placement="bottom-start"
                modifiers={[
                    { name: 'flip', enabled: false },
                    { name: 'preventOverflow', enabled: true, options: { altBoundary: true, tether: false } }
                ]}
                ref={popperRef}
                style={{
                    ...props.style,
                    maxHeight: maxHeight ? maxHeight : undefined,
                    overflowY: 'auto'
                }}
            />
        );
    }

    // Download Excel handler for bulk mode
    const handleDownloadExcel = async () => {
        try {
            await handleSaveFileNumber();
            // Generate Excel file using ExcelJS
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet("Family Numbers");

            // Add header row
            worksheet.addRow(["Family Number"]);

            // Add data rows
            fileNumbers.forEach(num => {
                worksheet.addRow([num]);
            });

            // Style header
            worksheet.getRow(1).eachCell(cell => {
                cell.font = { bold: true, color: { argb: "FF1A237E" } };
            });

            // Auto width for column
            worksheet.columns.forEach(column => {
                let maxLength = 0;
                column.eachCell({ includeEmpty: true }, cell => {
                    const cellValue = cell.value ? cell.value.toString() : "";
                    maxLength = Math.max(maxLength, cellValue.length);
                });
                column.width = maxLength + 2;
            });

            // Download
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "family_numbers.xlsx";
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            // handleSaveFileNumber already toasts error
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: '#f6f9fc',
                py: 4,
                px: { xs: 1, sm: 2 },
            }}
        >
            <Box sx={{ maxWidth: 900, mx: 'auto' }}>
                {/* Heading */}
                <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                        fontWeight: 500,
                        color: '#222',
                        mb: 2,
                        mt: 1,
                        fontSize: { xs: 20, sm: 24 }
                    }}
                >
                    New Family Identifier Request Form
                </Typography>
                <Paper
                    elevation={0}
                    sx={{
                        background: '#fff',
                        borderRadius: 2,
                        boxShadow: '0 2px 8px 0 rgba(60,72,100,0.07)',
                        p: { xs: 2, sm: 4 },
                        '&, & *': { fontSize: 16 },
                    }}
                >
                    <FormControl sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mb: 2 }}>
                        <RadioGroup
                            value={mode}
                            onChange={(e) => setMode(e.target.value)}
                            row
                        >
                            <FormControlLabel value="single" control={<Radio />} label="Single" sx={{ mr: 2 }} />
                            <FormControlLabel value="bulk" control={<Radio />} label="Bulk" />
                        </RadioGroup>
                    </FormControl>
                    {mode === 'single' ? (
                        <>
                            {/* ...existing single form... */}
                            <Typography variant="body2" sx={{ mb: 2, color: '#222'  }}>
                                I confirm that I have run a search in the {window.location.hostname.includes("crowell") ? "Crowell docket" : "docket"} to identify related matters. I request a new unique 5-digit family identifier for the following reason (check one)<span style={{color: 'red'}}>* </span>:
                            </Typography>
                            <FormControl component="fieldset" sx={{ mb: 2 }}>
                                <RadioGroup
                                    value={reason}
                                    onChange={e => setReason(e.target.value)}
                                >
                                    <Box display="flex" alignItems="center">
                                        <FormControlLabel
                                            value="No Priority"
                                            control={<Radio />}
                                            // label="No Priority"
                                            label={<Typography variant="body2">No Priority</Typography>}
                                        />
                                        <Tooltip title="This application is an original filing and has no prior filings in any jurisdiction and this is the start of a new family.">
                                            <IconButton size="small">
                                                <InfoOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                    <Box display="flex" alignItems="center">
                                        <FormControlLabel
                                            value="Priority Filed by Other"
                                            control={<Radio />}
                                            // label="Priority Filed by Other"
                                            label={<Typography variant="body2" >Priority Filed by Other</Typography>}
                                        />
                                        <Tooltip title="This application is based on a prior application that was not filed by Crowell and this is the start of a new family at Crowell.">
                                            <IconButton size="small">
                                                <InfoOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                    <Box display="flex" alignItems="center">
                                        <FormControlLabel
                                            value="Special Circumstance, General, or Litigation"
                                            control={<Radio />}
                                            label={<Typography variant="body2" >Special Circumstance, General, or Litigation</Typography>} // label="Special Circumstance, General, or Litigation"
                                        />
                                        <Tooltip title="There are existing related matters under a different family identifier or numbering system, and management has given special approval to issue a new 5-digit family identifier.">
                                            <IconButton size="small">
                                                <InfoOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </RadioGroup>
                            </FormControl>
                        </>
                    ) : (
                        <FormControl required sx={{ mb: 2 }}>
                            <Typography sx={{ mb: 1, fontWeight: 500, color: '#222' }}>
                                Number of Families to be generated<span style={{ color: '#d32f2f' }}> *</span>
                            </Typography>
                            <TextField
                                type="text"
                                value={bulkCount}
                                onChange={e => {
                                    const val = e.target.value;
                                    if (val === '') {
                                        setBulkCount('');
                                    } else {
                                        const num = parseInt(val, 10);
                                        setBulkCount(isNaN(num) ? '' : num);
                                    }
                                }}
                                inputProps={{ min: 1, max: 50 }}
                                error={bulkCount === '' || bulkCount < 1 || bulkCount > 50}
                                helperText={
                                    bulkCount === ''
                                        ? 'This field is required.'
                                        : (bulkCount < 1 || bulkCount > 50)
                                            ? 'Value must be between 1 and 50.'
                                            : 'You can generate maximum 50 families at a time.'
                                }
                                required
                                sx={{ width: { xs: '100%', sm: 400 } }}
                            />
                        </FormControl>
                    )}
                    <Divider sx={{ my: 2 }} />
                    <FormControl required component="fieldset" sx={{ mb: 2 }}>
                        <Typography sx={{ mb: 1, fontWeight: 500, color: '#222' }}>
                            Select Sub Org<span style={{ color: '#d32f2f' }}> *</span>
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 4 }}>
                            {subOrgOptions.map(opt => (
                                <FormControlLabel
                                    key={opt.value}
                                    control={
                                        <Checkbox
                                            checked={subOrg.includes(opt.value)}
                                            onChange={e => {
                                                let newValue = e.target.checked
                                                    ? [...subOrg, opt.value]
                                                    : subOrg.filter(v => v !== opt.value);
                                                if (newValue.length === 2) {
                                                    newValue = ['US', 'UK'];
                                                }
                                                setSubOrg(newValue);
                                            }}
                                        />
                                    }
                                    label={opt.label}
                                />
                            ))}
                        </Box>
                    </FormControl>
                    <Divider sx={{ my: 2 }} />
                    <FormControl required sx={{ width: '100%' }}>
                        <Autocomplete
                            options={filteredIpTypeOptions}
                            getOptionLabel={(option) => option.label}
                            value={
                                filteredIpTypeOptions.find(opt => opt.value === ipType) || null
                            }
                            onChange={(e, newValue) => setIpType(newValue ? newValue.value : '')}
                            isOptionEqualToValue={(option, value) => option.value === value}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="IP Type"
                                    required
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                        width: { xs: '100%', sm: 400 },
                                        backgroundColor: '#fafbfc'
                                    }}
                                />
                            )}
                            clearOnEscape
                            disablePortal={false}
                            ListboxProps={{
                                style: {
                                    overflowY: 'auto'
                                }
                            }}
                            PaperComponent={(props) => (
                                <Paper
                                    {...props}
                                    sx={{
                                        overflowY: 'auto',
                                        ...props.sx,
                                    }}
                                />
                            )}
                            PopperComponent={AdaptivePopper}
                        />
                    </FormControl>
                </Paper>
                {/* Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                    <Button variant="text" color="primary" sx={{ minWidth: 80 }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={isGenerateDisabled}
                        onClick={handleGenerate}
                        sx={{ minWidth: 110, fontWeight: 500 }}
                    >
                        {loading ? 'Generating...' : 'Generate'}
                    </Button>
                </Box>
                {/* Modal */}
                <Dialog
                    open={modalOpen}
                    onClose={() => {}}
                    sx={{
                        '& .MuiDialog-container': { justifyContent: 'center' },
                        '&, & *': { fontSize: '1.05rem' },
                    }}
                >
                    <DialogContent sx={{ minWidth: 320 }}>
                        {mode === 'single' ? (
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                The family number is {fileNumber}
                            </Typography>
                        ) : (
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                {fileNumbers.length === 1
                                    ? `The family number is ${fileNumbers[0]}`
                                    : `Family numbers generated: ${displayBulkNumbers}`}
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'center', pb: 2, pt: 1 }}>
                        <Button variant="outlined" color="error" onClick={handleDeleteFileNumber}>
                            Delete
                        </Button>
                        {mode === 'single' ? (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={async () => {
                                    try {
                                        await handleSaveFileNumber();
                                        navigator.clipboard.writeText(fileNumber);
                                    } catch (e) {
                                        // handleSaveFileNumber already toasts error
                                    }
                                }}
                            >
                                Copy to Clipboard
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleDownloadExcel}
                            >
                                Download Excel
                            </Button>
                        )}
                    </DialogActions>
                </Dialog>
            </Box>
        </Box>
    );
}